import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useWorkspace } from '~/contexts';
import { useAuth, useFeatures } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { colors } from '~/styles';
import { TRANSITION, useAssistant } from '../ai/assistant/AssistantContext';
import BillingArea from '../billing/BillingArea';
import { ClientsArea } from '../clients';
import { ExpensesArea } from '../expenses';
import HomeArea from '../home/HomeArea';
import PipelineArea from '../pipeline/PipelineArea';
import { ProjectsArea } from '../projects';
import { ReportsArea } from '../reports';
import { ReportsProvider } from '../reports/ReportsContext';

import ResourcesArea from '../resources/ResourcesArea';
import { WorkspaceSettingsArea } from '../settings';
import { MemberDetailsArea } from '../settings/members';
import { TimeArea } from '../time';
import WorkspaceBillingLocked from './WorkspaceBillingLocked';
import WorkspaceDisabled from './WorkspaceDisabled';

const Main = styled.main`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: ${({ hasMessage }) => (hasMessage ? '5.625rem' : '3.125rem')};
  margin-left: ${({ sidebarExpanded }) => (sidebarExpanded ? '10.3125rem' : '3.125rem')};
  margin-right: ${({ $panelOffset }) => $panelOffset}px;
  height: calc(100vh - ${({ hasMessage }) => (hasMessage ? '5.625rem' : '3.125rem')});
  overflow-y: scroll;
  background-color: ${colors.white};
  transition: ${({ $dragging }) => ($dragging ? 'none' : `margin-right ${TRANSITION}`)};
  container-type: inline-size;
  container-name: workspace;
`;

function WorkspaceMain({ sidebarExpanded }) {
  const features = useFeatures();
  const auth = useAuth();
  const { workspace, hasMessage } = useWorkspace();
  const { path, url } = useRouteMatch();
  const { panelWidth, isDragging } = useAssistant();

  useEffect(() => {
    document.body.classList.add('no-body-scroll');
    return () => document.body.classList.remove('no-body-scroll');
  }, []);

  if (workspace.statusId === 'disabled') {
    return (
      <Main
        id="workspace-main"
        hasMessage={hasMessage}
        sidebarExpanded={sidebarExpanded}
        $panelOffset={panelWidth}
        $dragging={isDragging}>
        <Switch>
          <Route path={path} exact>
            <WorkspaceDisabled />
          </Route>
          <Route>
            <Redirect to={url} />
          </Route>
        </Switch>
      </Main>
    );
  }

  if (!workspace.billingStatus?.isValid) {
    return (
      <Main
        id="workspace-main"
        hasMessage={hasMessage}
        sidebarExpanded={sidebarExpanded}
        $panelOffset={panelWidth}
        $dragging={isDragging}>
        <Switch>
          <Route path={path} exact>
            <WorkspaceBillingLocked />
          </Route>

          <Route path={`${path}/settings`}>
            {auth.workspace.manage ? <WorkspaceSettingsArea /> : <ErrorPage.Forbidden publicSite={false} />}
          </Route>

          <Route>
            <Redirect to={url} />
          </Route>
        </Switch>
      </Main>
    );
  }

  return (
    <Main
      id="workspace-main"
      hasMessage={hasMessage}
      sidebarExpanded={sidebarExpanded}
      $panelOffset={panelWidth}
      $dragging={isDragging}>
      <Switch>
        <Route
          path={[
            '/dashboard',
            '/dashboard/executive',
            '/dashboard/personal',
            '/my-projects',
            '/my-tasks',
            '/my-allocations',
            '/my-allocations/view/:allocationId',
            '/my-profile',
            '/my-profile/change-password',
            '/my-profile/two-factor-auth',
            '/my-profile/two-factor-auth/setup',
            '/my-profile/calendar',
            '/my-profile/connected-apps',
          ].map((p) => `${path}${p}`)}
          exact>
          <HomeArea routeMatch={{ path, url }} />
        </Route>

        <CustomRedirect path={`${path}/my-exports`} to={`${url}/reports/exports`} exact />

        <Route path={`${path}/time`}>
          <TimeArea />
        </Route>

        <Route path={`${path}/portfolio/projects`}>
          <ProjectsArea />
        </Route>

        <Route path={`${path}/portfolio/clients`}>
          <ClientsArea />
        </Route>

        <Redirect from={`${path}/portfolio`} to={`${url}/portfolio/projects`} exact />

        {/* Legacy /projects and /clients routes — redirect to /portfolio */}
        <Route path={`${path}/projects`}>
          <PortfolioRedirect segment="projects" />
        </Route>
        <Route path={`${path}/clients`}>
          <PortfolioRedirect segment="clients" />
        </Route>

        {features.expenseReporting && (
          <Route path={`${path}/expenses`}>
            <ExpensesArea />
          </Route>
        )}

        <Route path={`${path}/billing`}>
          <BillingArea />
        </Route>

        {/* Legacy /invoices routes */}
        <CustomRedirect path={`${path}/invoices`} to={`${url}/billing`} exact />
        <CustomRedirect path={`${path}/invoices/ready-to-bill`} to={`${url}/billing/ready-to-bill`} exact />
        <CustomRedirect path={`${path}/invoices/list`} to={`${url}/billing/invoices`} exact />
        <CustomRedirect path={`${path}/invoices/list/:invoiceId`} to={`${url}/billing/invoices/:invoiceId`} exact />
        <CustomRedirect path={`${path}/invoices/payments`} to={`${url}/billing/payments`} exact />
        <CustomRedirect
          path={`${path}/invoices/payments/:paymentId/view`}
          to={`${url}/billing/payments/:paymentId/view`}
          exact
        />
        <CustomRedirect
          path={`${path}/invoices/payments/:paymentId/edit`}
          to={`${url}/billing/payments/:paymentId/edit`}
          exact
        />

        {features.pipeline && (
          <Route path={`${path}/pipeline`}>
            {auth.pipeline.view ? <PipelineArea /> : <ErrorPage.Forbidden publicSite={false} />}
          </Route>
        )}

        {features.allocations && (
          <Route path={`${path}/resources`}>
            {auth.allocations.view ? <ResourcesArea /> : <ErrorPage.Forbidden publicSite={false} />}
          </Route>
        )}

        <Route path={`${path}/reports`}>
          <ReportsProvider>
            <ReportsArea />
          </ReportsProvider>
        </Route>

        <Route path={`${path}/settings/members/details/:memberId`}>
          {auth.workspace.manage ? <MemberDetailsArea /> : <ErrorPage.Forbidden publicSite={false} />}
        </Route>

        <Route path={`${path}/settings`}>
          {auth.workspace.manage ? <WorkspaceSettingsArea /> : <ErrorPage.Forbidden publicSite={false} />}
        </Route>

        <Route path={path} exact>
          <Redirect to={`${url}/dashboard`} />
        </Route>

        <Route>
          <ErrorPage.NotFound publicSite={false} />
        </Route>
      </Switch>
    </Main>
  );
}

function CustomRedirect(props) {
  const location = useLocation();
  return <Redirect to={{ ...location, pathname: props.to }} {...props} />;
}

function PortfolioRedirect({ segment }) {
  const location = useLocation();
  const newPath = location.pathname.replace(`/${segment}`, `/portfolio/${segment}`);
  return <Redirect to={{ ...location, pathname: newPath }} />;
}

export default WorkspaceMain;
